import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { fdt, fdate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { StatusBadge } from "@/components/pawkt/StatusBadge";
import { ReasonChips } from "@/components/pawkt/VisitReasons";
import { Stethoscope, PawPrint, Link2Off } from "lucide-react";
import { toast } from "sonner";

const TABS = [["today", "Today"], ["upcoming", "Upcoming"], ["booked", "New / confirmed"], ["waiting", "Waiting / arrived"],
  ["in_consultation", "In consultation"], ["completed", "Completed"], ["history", "Cancelled / no-show"]];
const NEXT = { scheduled: ["waiting", "Mark arrived"], waiting: ["in_consultation", "Start consultation"], in_consultation: ["completed", "Complete consultation"] };

function QueueRow({ e, onOpen }) {
  const qc = useQueryClient();
  const nx = NEXT[e.status];
  const go = useMutation({
    mutationFn: () => api.put(`/veterinary/encounters/${e.id}`, { status: nx[0] }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["vet-my-queue"] }); toast.success("Updated"); },
    onError: (err) => toast.error(apiErr(err)),
  });
  return (
    <div className="flex flex-wrap items-center gap-3 py-3" data-testid={`vet-my-row-${e.id}`}>
      <button onClick={onOpen} className="min-w-0 flex-1 text-left" data-testid={`vet-my-open-${e.id}`}>
        <p className="text-sm font-semibold text-slate-900">{fdt(e.appointment_at || e.visit_date)} · {e.pet_name || "Pet"}<span className="font-normal text-slate-500"> · {e.species || "—"} · {e.customer_name || "—"}</span></p>
        <p className="text-xs text-slate-500">{e.service_name || (e.encounter_type || "consultation").replace("_", " ")}{e.branch_name ? ` · ${e.branch_name}` : ""}</p>
        <ReasonChips reasons={e.visit_reasons} testid={`vet-my-reasons-${e.id}`} />
      </button>
      <StatusBadge status={e.status} />
      {nx && <button onClick={() => (nx[0] === "completed" ? onOpen() : go.mutate())} disabled={go.isPending} data-testid={`vet-my-next-${e.id}`}
        className="rounded-full bg-slate-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-slate-700">{nx[1]}</button>}
    </div>
  );
}

function Patients() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const { data, isLoading } = useQuery({ queryKey: ["vet-my-patients", search.trim()], queryFn: async () => (await api.get("/veterinary/my-patients", { params: { search: search.trim() } })).data });
  const items = data?.items || [];
  return (
    <div className="rounded-2xl border border-pawkt-border bg-white p-4" data-testid="vet-my-patients">
      <p className="font-display font-bold text-pawkt-text">My patients</p>
      <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pet or owner…" data-testid="vet-my-patients-search" className="mt-2 w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2 text-sm" />
      {isLoading ? <Loading /> : !items.length ? <p className="mt-3 text-sm text-slate-500">No patients seen yet.</p> : (
        <div className="mt-2 divide-y divide-slate-100">{items.map((p) => (
          <button key={p.pet_id} onClick={() => navigate(`/pets/${p.pet_id}`)} className="flex w-full flex-wrap items-center justify-between gap-2 py-2.5 text-left hover:bg-slate-50" data-testid={`vet-patient-${p.pet_id}`}>
            <span className="min-w-0"><span className="text-sm font-semibold text-slate-900"><PawPrint className="mr-1 inline h-3.5 w-3.5 text-slate-400" />{p.pet_name}</span><span className="text-xs text-slate-500"> · {p.species}{p.breed ? ` / ${p.breed}` : ""} · {p.customer_name}{p.customer_mobile ? ` · ${p.customer_mobile}` : ""}</span>
              {p.allergies && <span className="block text-xs text-rose-600">Allergies: {p.allergies}</span>}</span>
            <span className="text-xs text-slate-500">{p.visits} visit{p.visits === 1 ? "" : "s"} · last {fdate(p.last_visit)}</span>
          </button>))}</div>
      )}
    </div>
  );
}

export default function VetHome() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [view, setView] = useState("today");
  const [search, setSearch] = useState("");
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({ queryKey: ["vet-my-queue", view, search.trim()], refetchInterval: 30000, refetchOnWindowFocus: true, staleTime: 0,
    queryFn: async () => (await api.get("/veterinary/my-queue", { params: { view, search: search.trim() } })).data });
  const open = (e) => navigate(`/veterinary?open=${e.id}`);
  if (data && !data.linked) {
    return (<div><PageHeader title={`Welcome${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`} subtitle="Your clinic day" />
      <Empty icon={Link2Off} title="Your login isn't linked to a staff profile yet" subtitle="Ask the owner to link your account to your vet profile in Staff Management so your appointments appear here." /></div>);
  }
  const c = data?.counts || {};
  return (
    <div className="space-y-5" data-testid="vet-home">
      <PageHeader title={`Welcome${user?.name ? `, Dr. ${user.name.split(" ")[0]}` : ""}`} subtitle="Your appointments and patients" />
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="vet-home-stats">
        {[["today", "Today"], ["waiting", "Waiting"], ["in_consultation", "In consultation"], ["completed", "Completed"]].map(([k, l]) => (
          <button key={k} onClick={() => setView(k)} className="rounded-2xl border border-pawkt-border bg-white p-4 text-left" data-testid={`vet-home-stat-${k}`}>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{l}</p><p className="mt-1 text-2xl font-extrabold tabular text-slate-900">{c[k] ?? 0}</p></button>))}
      </div>
      <div className="rounded-2xl border border-pawkt-border bg-white p-4">
        <div className="flex gap-1.5 overflow-x-auto pb-1" data-testid="vet-home-tabs">
          {TABS.map(([k, l]) => <button key={k} onClick={() => setView(k)} data-testid={`vet-home-tab-${k}`}
            className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold ${view === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"}`}>{l} ({c[k] ?? 0})</button>)}
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search pet or owner…" data-testid="vet-home-search" className="mt-3 w-full max-w-sm rounded-lg border border-slate-200 px-3 py-2 text-sm" />
        {isLoading ? <Loading /> : isError ? (
          <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" data-testid="vet-home-error">
            Couldn't load your appointments: {apiErr(error)}
            <button onClick={() => refetch()} disabled={isFetching} data-testid="vet-home-retry" className="ml-3 rounded-full bg-rose-700 px-3 py-1 text-xs font-semibold text-white">Retry</button>
          </div>
        ) : !(data?.items || []).length ? <Empty icon={Stethoscope} title="Nothing here" subtitle="Appointments assigned to you will appear here." /> : (
          <div className="divide-y divide-slate-100" data-testid={`vet-home-list-${view}`}>{data.items.map((e) => <QueueRow key={e.id} e={e} onOpen={() => open(e)} />)}</div>
        )}
      </div>
      <Patients />
    </div>
  );
}
